import { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Autocomplete, Box, Button,
  Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, LinearProgress, Link, MenuItem, Paper, Radio, Select, Stack, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NOMBRE_FAMILIA, NOMBRE_IDIOMA, PREFERENCIAS_VERSION_POR_DEFECTO, RESOLUTION_LADDER,
  bandaDe, normalizarContenedor,
} from '@film/shared';
import { api } from '../lib/api.js';
import type {
  AjustesApp, ApiFallo, BibliotecaPlex, DecisionAuto, PinPlex, PreferenciasVersion,
  VerificacionRutas,
} from '../lib/api.js';
import { Link as RouterLink } from 'react-router-dom';
import { formatearTamano } from '../lib/eventos.js';

const ALTURAS = [
  { v: 720, t: '720p — reescala todo lo que pase de 720p' },
  { v: 1080, t: '1080p — recomendado: el 4K sale como 1080p' },
  { v: 2160, t: '2160p — no reescala nada; un 4K tarda horas sin GPU' },
];

/** El tope lo pone el servidor (`transcodeConcurrency`, máximo 8). Más allá de dos o tres
 *  no queda CPU ni para reproducir, así que ofrecer más sería ofrecer un problema. */
const SIMULTANEAS = [1, 2, 3, 4];

/** Los idiomas de pista se escriben en ISO 639-2, que es lo que traen los ficheros y lo que
 *  entiende Plex. Se ofrecen los habituales y se admite cualquier otro a mano. */
const IDIOMAS_PISTA = ['spa', 'eng', 'fra', 'deu', 'ita', 'por', 'cat', 'glg', 'eus', 'jpn', 'zho', 'rus'];

/** Bloques de un mismo subtítulo leyéndose a la vez (D-19). Más allá de cuatro se compite
 *  con la reproducción de Plex, que es lo que no se puede permitir este servidor. */
const BLOQUES_OCR = [1, 2, 3, 4, 6, 8];

const MODOS_AUTO = [
  { v: 'apagado', t: 'Apagado — solo lo que marques película a película' },
  { v: 'simulacion', t: 'Simulación — apunta qué haría, sin descargar nada' },
  { v: 'activo', t: 'Activo — descarga sola lo que no tenga ninguna duda' },
];

const ETIQUETA_DECISION: Record<DecisionAuto['result'], string> = {
  encolada: 'descargada',
  simulada: 'se habría bajado',
  recomendada: 'para ti',
  sin_candidata: 'nada que valga',
};

const COLOR_DECISION: Record<DecisionAuto['result'], 'success' | 'info' | 'warning' | 'default'> = {
  encolada: 'success',
  simulada: 'info',
  recomendada: 'warning',
  sin_candidata: 'default',
};

/** Por qué no se encoló. Se dice con palabras: `cupo` no significa nada a los tres meses. */
const FRENO: Record<string, string> = {
  identidad: 'la ficha no cruza por IMDb',
  empate: 'dos versiones a la par',
  ninguna_valida: 'ninguna versión pasa los frenos',
  simulacion: 'estamos en simulación',
  cupo: 'ya hay bastantes bajando',
  espacio: 'no cabe en el disco',
  apagado: 'la automática está apagada',
  sin_fichero: 'sin fichero que encolar',
  ya_la_tienes: 'ya la tienes en Plex',
  ya_la_tienes_mejorable: 'ya la tienes, pero hay mejor calidad',
};

/** Cuánto se aprieta la banda de bitrate. La banda ya viene medida; esto solo la abre o
 *  la cierra entera, que es más fácil de decidir que diez números sueltos. */
const TOLERANCIAS = [
  { v: 'exigente', t: 'Exigente — solo lo que está en su sitio' },
  { v: 'normal', t: 'Normal — la banda medida (recomendado)' },
  { v: 'tolerante', t: 'Tolerante — avisa solo de lo escandaloso' },
];

/** La banda de bitrate resultante, en números. Un desplegable de tres palabras no dice qué
 *  va a pasar con las películas; esto lo enseña antes de guardar. */
function muestraBanda(p: PreferenciasVersion, codec: string): string {
  const banda = bandaDe(
    { resolution: p.resolucionIdeal, container: null, videoCodec: codec, audioLangs: [], files: [] },
    p,
  );
  if (!banda) return '—';
  const n = (x: number) => (x >= 10 ? x.toFixed(0) : x.toFixed(1).replace('.', ','));
  return `${n(banda.min)} y ${n(banda.max)} Mbps`;
}

/** Los tres niveles con los que se colorea la ficha, dichos como los diría alguien. */
const NIVELES = [
  { v: 'ideal', t: 'La quiero' },
  { v: 'aceptable', t: 'Me vale' },
  { v: 'rechazada', t: 'No me vale' },
];

function Fila({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary">{etiqueta}</Typography>
      <Typography variant="caption">{valor}</Typography>
    </Stack>
  );
}

/** El resultado de comprobar las rutas contra un fichero REAL de la biblioteca.
 *  No dice solo si va bien: dice qué implica que no vaya. */
function ResultadoRutas({ r }: { r: VerificacionRutas }) {
  const marca = (ok: boolean) => (ok ? '✓' : '✗');
  return (
    <Alert severity={r.ok ? (r.sameFilesystemAsConversions ? 'success' : 'warning') : 'error'} sx={{ mt: 1 }}>
      <AlertTitle>
        {r.ok
          ? r.sameFilesystemAsConversions
            ? 'Rutas verificadas · mover será instantáneo'
            : 'Rutas verificadas · habrá que copiar'
          : 'Las rutas no funcionan'}
      </AlertTitle>
      <Stack spacing={0.25} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
        <Box>Fichero de muestra: {r.sampledFile ?? '—'}</Box>
        <Box>Lo vemos en: {r.resolvedLocal ?? '—'}</Box>
        <Box>{marca(r.exists)} existe para nosotros</Box>
        <Box>{marca(r.writableDir)} carpeta escribible</Box>
        <Box>{marca(r.sameFilesystemAsConversions)} mismo sistema de ficheros que las conversiones</Box>
      </Stack>
      {r.problema && <Typography variant="body2" sx={{ mt: 1 }}>{r.problema}</Typography>}
    </Alert>
  );
}

function BibliotecaFila({ lib, onCambio }: { lib: BibliotecaPlex; onCambio: () => void }) {
  const [verificacion, setVerificacion] = useState<VerificacionRutas | null>(null);
  const [rutas, setRutas] = useState({ plexPath: lib.plexPath ?? '', localPath: lib.localPath ?? '' });

  const rutasCambiadas =
    rutas.plexPath !== (lib.plexPath ?? '') || rutas.localPath !== (lib.localPath ?? '');

  const cambiar = useMutation({
    mutationFn: (cambios: Record<string, unknown>) => api.actualizarBiblioteca(lib.id, cambios),
    onSuccess: () => {
      setVerificacion(null);
      onCambio();
    },
  });
  const verificar = useMutation({
    mutationFn: () => api.verificarRutas(lib.id),
    onSuccess: (r) => setVerificacion(r),
  });

  const mostrada = verificacion ?? lib.pathVerifyResult;

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* El Chip es un <div> y el Typography, un <p>: anidarlos es HTML inválido y React
              avisa. Van como hermanos en una fila, que además es lo que ya aparentaban. */}
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {lib.title}
            </Typography>
            <Chip size="small" variant="outlined" label={lib.type === 'movie' ? 'películas' : 'series'} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            sección {lib.sectionKey}
            {lib.pathVerifiedAt && ` · rutas verificadas ${new Date(lib.pathVerifiedAt).toLocaleDateString('es-ES')}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Tooltip title="Si está apagada, la app NO considera poseído nada de esta biblioteca.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={lib.countsAsOwned}
                  onChange={(e) => cambiar.mutate({ countsAsOwned: e.target.checked })}
                />
              }
              label={<Typography variant="caption">Cuenta</Typography>}
            />
          </Tooltip>
          <Tooltip title="La que viene marcada al colocar desde Conversiones. Se puede cambiar en cada película.">
            <FormControlLabel
              control={
                <Radio
                  size="small"
                  checked={lib.importTarget}
                  onChange={() => cambiar.mutate({ importTarget: true })}
                />
              }
              label={<Typography variant="caption">Sugerida</Typography>}
            />
          </Tooltip>
          <Button size="small" disabled={verificar.isPending} onClick={() => verificar.mutate()}>
            {verificar.isPending ? 'Comprobando…' : 'Verificar rutas'}
          </Button>
        </Stack>
      </Stack>

      {/* Las dos rutas del mapeo, editables. Lo normal es que coincidan —montamos la
          biblioteca donde la ve Plex— pero si los montajes no se pueden igualar hay que
          poder corregirlo aquí en vez de tocar la base de datos. */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5, alignItems: { md: 'flex-end' } }}>
        <TextField
          size="small" fullWidth label="Ruta que publica Plex"
          value={rutas.plexPath}
          onChange={(e) => setRutas({ ...rutas, plexPath: e.target.value })}
          placeholder="/data/Peliculas_Carlos"
          helperText="El prefijo que aparece en los ficheros según Plex"
        />
        <TextField
          size="small" fullWidth label="Ruta donde la vemos nosotros"
          value={rutas.localPath}
          onChange={(e) => setRutas({ ...rutas, localPath: e.target.value })}
          placeholder="/data/Peliculas_Carlos"
          helperText="Dónde está montada para esta aplicación. Sin ella no se puede mover nada aquí"
        />
        <Stack direction="row" spacing={1}>
          <Button
            size="small" variant="contained"
            disabled={!rutasCambiadas || cambiar.isPending}
            onClick={() =>
              cambiar.mutate({
                plexPath: rutas.plexPath.trim() || null,
                localPath: rutas.localPath.trim() || null,
              })
            }
          >
            Guardar
          </Button>
          {rutasCambiadas && (
            <Button size="small" onClick={() => setRutas({ plexPath: lib.plexPath ?? '', localPath: lib.localPath ?? '' })}>
              Deshacer
            </Button>
          )}
        </Stack>
      </Stack>

      {rutasCambiadas && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          Hay cambios sin guardar. Al guardarlos se descarta la verificación anterior:
          conviene volver a verificar.
        </Typography>
      )}

      {cambiar.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>{(cambiar.error as ApiFallo).message}</Alert>
      )}
      {verificar.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>{(verificar.error as ApiFallo).message}</Alert>
      )}
      {mostrada && <ResultadoRutas r={mostrada} />}
    </Box>
  );
}

function SeccionAmule() {
  const { data, error } = useQuery({
    queryKey: ['amule-status'],
    queryFn: api.estadoAmule,
    refetchInterval: 10_000,
    retry: false,
  });

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>aMule</Typography>
        <Alert severity="error">{(error as ApiFallo).message}</Alert>
      </Paper>
    );
  }
  if (!data) return null;

  const r = data.red;
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>aMule</Typography>

      {/* La ID baja es la causa número uno de «esto va lentísimo», y no se ve por
          ninguna parte salvo que se diga aquí. */}
      {r && !r.ed2k.highId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>ID baja</AlertTitle>
          Solo puedes conectarte a clientes con ID alta, así que la mitad de las fuentes de
          cualquier película quedan fuera de tu alcance. Se arregla abriendo los puertos
          <code> 4662/tcp </code> y <code> 4672/udp </code> en el router.
        </Alert>
      )}

      <Stack spacing={1}>
        <Fila etiqueta="Red eD2k" valor={
          <Chip size="small" label={r?.ed2k.state ?? '—'}
            color={r?.ed2k.state === 'connected' ? 'success' : 'warning'} />
        } />
        <Fila etiqueta="Servidor" valor={r?.ed2k.serverName ?? '—'} />
        <Fila etiqueta="Kad" valor={
          <Chip size="small" label={r?.kad.state ?? '—'}
            color={r?.kad.state === 'connected' ? 'success' : 'warning'} />
        } />
        <Fila etiqueta="ID" valor={
          <Chip size="small" label={r?.ed2k.highId ? 'alta' : 'baja'}
            color={r?.ed2k.highId ? 'success' : 'warning'} />
        } />
        <Divider sx={{ my: 0.5 }} />
        <Fila etiqueta="Bajando" valor={`${formatearTamano(r?.speeds.downloadBps ?? 0)}/s`} />
        <Fila etiqueta="Subiendo" valor={`${formatearTamano(r?.speeds.uploadBps ?? 0)}/s`} />
        <Fila etiqueta="En la cola de aMule" valor={`${data.transferencias} transferencia(s)`} />
        <Fila
          etiqueta="Recibiendo datos ahora"
          valor={`${data.activas} de ${data.transferencias}`}
        />
        {data.sinFuentes > 0 && (
          <Fila
            etiqueta="Sin ninguna fuente"
            valor={<Typography variant="caption" color="warning.main">{data.sinFuentes}</Typography>}
          />
        )}
        <Fila etiqueta="Disco libre en descargas" valor={
          r?.diskFreeBytes != null ? formatearTamano(r.diskFreeBytes) : '—'
        } />
        <Fila etiqueta="Categoría de la app" valor={data.categoria != null ? `#${data.categoria}` : 'sin crear'} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Cuánto has subido y bajado, el ratio y la gráfica de velocidades están en{' '}
        <Link component={RouterLink} to="/red">Red</Link>.
      </Typography>
    </Paper>
  );
}

function SeccionConversion() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ajustes'], queryFn: api.ajustes });
  const [borrador, setBorrador] = useState<Partial<AjustesApp>>({});

  const guardar = useMutation({
    mutationFn: (c: Partial<AjustesApp>) => api.guardarAjustes(c),
    onSuccess: () => {
      setBorrador({});
      void qc.invalidateQueries({ queryKey: ['ajustes'] });
    },
  });

  const recoger = useMutation({
    mutationFn: api.recogerOriginales,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ajustes'] }),
  });

  if (!data) return null;
  const a = { ...data.settings, ...borrador };
  const sinGuardar = Object.keys(borrador).length > 0;
  const set = (c: Partial<AjustesApp>) => setBorrador({ ...borrador, ...c });

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Conversión a MP4</Typography>

      {!data.transcodeDisponible && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La conversión está desactivada en el entorno (<code>TRANSCODE_ENABLED</code>).
          Las películas se importan tal cual llegan.
        </Alert>
      )}

      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Switch
              checked={a.transcodeEnabled}
              onChange={(e) => set({ transcodeEnabled: e.target.checked })}
            />
          }
          label="Convertir a MP4 antes de dejarlas en la carpeta de conversiones"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          Lo que ya venga en H.264 + AC3 solo cambia de contenedor: tarda segundos y no
          pierde calidad. Solo se recodifica de verdad lo que hace falta.
        </Typography>

        <Box>
          <Typography variant="body2" gutterBottom>Altura máxima del vídeo</Typography>
          <Select
            size="small"
            value={a.transcodeMaxHeight}
            onChange={(e) => set({ transcodeMaxHeight: Number(e.target.value) })}
            sx={{ minWidth: 300 }}
            disabled={!a.transcodeEnabled}
          >
            {ALTURAS.map((x) => <MenuItem key={x.v} value={x.v}>{x.t}</MenuItem>)}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Lo que pase de esa altura se recodifica reescalado a ella, aunque ya viniera en
            H.264. Codificar un 1080p cuesta cuatro veces menos que un 2160p, y este servidor
            no tiene GPU: a 2160p ocuparía la máquina varias horas compitiendo con Plex.
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" gutterBottom>Conversiones a la vez</Typography>
          <Select
            size="small"
            value={a.transcodeConcurrency}
            onChange={(e) => set({ transcodeConcurrency: Number(e.target.value) })}
            sx={{ minWidth: 300 }}
            disabled={!a.transcodeEnabled}
          >
            {SIMULTANEAS.map((n) => (
              <MenuItem key={n} value={n}>
                {n === 1 ? 'Una cada vez (recomendado)' : `${n} a la vez`}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Lo que no cabe espera en la cola y entra solo cuando queda sitio; se ve en
            Conversiones. Una cada vez es lo sensato recodificando por CPU: dos tardan más
            que una detrás de otra y dejan a Plex sin nada con qué reproducir. Subirlo compensa
            con codificador por hardware, o cuando casi todo son remuxes, que van de disco.
            Cambiarlo tiene efecto al momento — nunca corta una conversión ya empezada.
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" gutterBottom>Codificador de vídeo</Typography>
          <Select
            size="small"
            value={a.transcodeVideoEncoder}
            onChange={(e) => set({ transcodeVideoEncoder: e.target.value as AjustesApp['transcodeVideoEncoder'] })}
            sx={{ minWidth: 300 }}
            disabled={!a.transcodeEnabled}
          >
            <MenuItem value="auto">Automático — por hardware si lo hay</MenuItem>
            <MenuItem value="libx264">libx264 — siempre por CPU</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Se pregunta a ffmpeg qué codificadores trae en vez de suponerlo: pedir uno que no
            está hace que la conversión muera al arrancar. En el contenedor Linux no hay
            ninguno por hardware y «automático» acaba en libx264 igualmente.
          </Typography>
        </Box>

        <Divider />

        <Typography variant="subtitle2">Pistas: orden y etiquetas</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Estas reglas son las que aplica la propuesta automática al convertir sin que nadie
          mire. En cada película se pueden revisar y corregir a mano desde Conversiones →
          Pistas.
        </Typography>

        <Box>
          <Typography variant="body2" gutterBottom>Idiomas preferidos, en orden</Typography>
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={IDIOMAS_PISTA}
            value={a.transcodeLangs}
            onChange={(_e, v) => set({ transcodeLangs: v.map((x) => x.trim().toLowerCase()).filter(Boolean) })}
            renderInput={(params) => <TextField {...params} placeholder="spa, eng…" />}
            sx={{ maxWidth: 460 }}
            disabled={!a.transcodeEnabled}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Manda en el ORDEN de las pistas del MP4, que no es cosmético: Plex arranca con la
            primera de audio, así que con el inglés delante la película se pone en inglés
            aunque traiga castellano. En ISO 639-2 (<code>spa</code>, <code>eng</code>).
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={a.transcodeOnlyPreferredAudio}
              disabled={!a.transcodeEnabled}
              onChange={(e) => set({ transcodeOnlyPreferredAudio: e.target.checked })}
            />
          }
          label="Quitar las pistas de audio que no estén entre las preferidas"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          Apagado por defecto: tirar un audio es irreversible y el que sobra solo ocupa. Si
          ninguna pista cumpliera el filtro se conservan todas — quedarse sin sonido sería
          peor que tener una de más.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={a.transcodeForcedDefault}
              disabled={!a.transcodeEnabled}
              onChange={(e) => set({ transcodeForcedDefault: e.target.checked })}
            />
          }
          label="Marcar por defecto el subtítulo forzado del idioma preferido"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          Es la diferencia entre poner la película y que traduzca sola los diálogos en otro
          idioma, o tener que ir al menú de subtítulos cada vez. El forzado se detecta aunque
          nadie lo haya marcado: pesa mucho menos que el completo del mismo idioma.
        </Typography>

        <Divider />

        <Typography variant="subtitle2">Subtítulos de imagen (OCR)</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          MP4 no admite VobSub ni PGS: sin pasarlos a texto se pierden. Los lee tesseract
          —<code>pgsrip</code> los PGS, <code>subtile-ocr</code> los VobSub— y un subtítulo que
          no se pueda leer para la conversión entera, en vez de dar una película a la que le
          falta una pista.
        </Typography>

        <Box>
          <Select
            size="small"
            value={a.ocrBlocksAtOnce}
            onChange={(e) => set({ ocrBlocksAtOnce: Number(e.target.value) })}
            sx={{ minWidth: 320 }}
            disabled={!a.transcodeEnabled}
          >
            {BLOQUES_OCR.map((n) => (
              <MenuItem key={n} value={n}>
                {n === 1 ? 'Un bloque a la vez — de uno en uno' : `${n} bloques a la vez`}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          Un subtítulo son cientos de imágenes y se leen por bloques: de ahí sale el
          porcentaje mientras corre, y leer varios a la vez es lo que aprovecha la máquina
          —tesseract por su cuenta apenas usa más de un núcleo—. Cuantos más, menos CPU le
          queda a Plex para reproducir.
        </Typography>

        {/* Qué hay INSTALADO de verdad. Sin esto, un «este subtítulo se descartará» parece
            un capricho en vez de una herramienta que falta. */}
        {data.ocr && (
          <Alert severity={data.ocr.pgs && data.ocr.vobsub ? 'success' : 'warning'} sx={{ py: 0.5 }}>
            Se pueden leer: PGS {data.ocr.pgs ? 'sí' : 'no'}, VobSub {data.ocr.vobsub ? 'sí' : 'no'}.
            {' '}Instalado: {[
              data.ocr.tesseract ? 'tesseract' : null,
              data.ocr.mkvextract ? 'mkvextract' : null,
            ].filter(Boolean).join(', ') || 'nada'}.
            {/* Tener tesseract no es tenerlo todo: cada idioma es un paquete aparte, y sin
                el suyo esa pista no se lee. Enseñarlos evita que un «se descartará» sobre un
                subtítulo perfectamente legible parezca un fallo. */}
            {' '}Idiomas de tesseract: {data.ocr.idiomas.length
              ? `${data.ocr.idiomas.join(', ')} (${data.ocr.idiomas.length})`
              : 'ninguno'}.
            {(!data.ocr.pgs || !data.ocr.vobsub) && (
              <> Lo que no se pueda leer se descarta al planificar, con su motivo: no se convierte
              una película a la que le vayan a faltar pistas.</>
            )}
            <> Un subtítulo de imagen en un idioma que no esté en esa lista se descarta igual:
            leerlo con otro modelo daría un SRT ilegible. Se añaden en el Dockerfile, un
            paquete por idioma.</>
          </Alert>
        )}

        <Divider />

        <Typography variant="subtitle2">El original tras convertir</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Convertir crea un fichero nuevo, así que durante un tiempo la película ocupa el
          doble. El original se queda compartiéndose en aMule y se retira después. Nunca antes
          de que la película esté colocada en una biblioteca: mientras espera en Conversiones,
          el original es la única forma de volver a convertirla.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small" type="number" label="Días compartiendo"
            value={a.seedDays}
            onChange={(e) => set({ seedDays: Number(e.target.value) })}
            helperText="0 = retirar en cuanto se convierta"
            sx={{ maxWidth: 220 }}
          />
          <TextField
            size="small" type="number" label="Retirar si quedan menos de (GB)"
            value={a.minFreeGb}
            onChange={(e) => set({ minFreeGb: Number(e.target.value) })}
            helperText="Se retiran los más antiguos primero"
            sx={{ maxWidth: 280 }}
          />
        </Stack>

        <Alert
          severity={data.originales.ficheros ? 'info' : 'success'}
          action={
            data.originales.ficheros > 0 && (
              <Button size="small" disabled={recoger.isPending} onClick={() => recoger.mutate()}>
                {recoger.isPending ? 'Recogiendo…' : 'Recoger ahora'}
              </Button>
            )
          }
        >
          {data.originales.ficheros
            ? `${data.originales.ficheros} original(es) compartiéndose, ${formatearTamano(data.originales.bytes)} ocupados.`
            : 'No hay originales pendientes de recoger.'}
        </Alert>

        {/* Los retenidos van aparte y NO en el mismo número: se arreglan de otra forma. Los
            recogibles se van solos con el tiempo; estos solo se desbloquean colocando su
            película, y esconderlos en un total ocultaría la única acción que sirve. */}
        {data.originales.retenidos > 0 && (
          <Alert severity="warning">
            <AlertTitle>
              {data.originales.retenidos} original(es) retenidos, {formatearTamano(data.originales.bytesRetenidos)}
            </AlertTitle>
            Su conversión sigue esperando en Conversiones. No se retiran —ni aunque falte
            espacio— porque son la única fuente para volver a convertir esas películas. Se
            liberan solos en cuanto las muevas a una biblioteca.
          </Alert>
        )}

        {recoger.data && (
          <Alert severity="success" onClose={() => recoger.reset()}>
            {recoger.data.retirados} retirado(s), {formatearTamano(recoger.data.bytesLiberados)} liberados
            {recoger.data.espacioLibreGb !== null && ` · ${recoger.data.espacioLibreGb.toFixed(0)} GB libres`}.
            {recoger.data.errores.length > 0 && ` ${recoger.data.errores.length} con problemas.`}
          </Alert>
        )}

        {guardar.isError && <Alert severity="error">{(guardar.error as ApiFallo).message}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" size="small"
            disabled={!sinGuardar || guardar.isPending}
            onClick={() => guardar.mutate(borrador)}
          >
            Guardar
          </Button>
          {sinGuardar && <Button size="small" onClick={() => setBorrador({})}>Deshacer</Button>}
        </Stack>
      </Stack>
    </Paper>
  );
}

const GB = 1024 ** 3;

/** Qué versión de HispaShare interesa.
 *
 *  Es lo que colorea la ficha, y solo eso: no filtra la tabla ni cambia lo que elige la
 *  watchlist por su cuenta. Se edita aquí y no en la ficha porque la respuesta es la misma
 *  para todas las películas —lo que se busca no cambia de un título a otro— y porque en la
 *  ficha, con quince versiones delante, un formulario tapa lo que se ha venido a mirar. */
function SeccionVersiones() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ajustes'], queryFn: api.ajustes });
  const [borrador, setBorrador] = useState<Partial<PreferenciasVersion>>({});

  const guardar = useMutation({
    mutationFn: (p: PreferenciasVersion) => api.guardarAjustes({ preferenciasVersion: p }),
    onSuccess: () => {
      setBorrador({});
      void qc.invalidateQueries({ queryKey: ['ajustes'] });
    },
  });

  if (!data) return null;
  const p = { ...data.settings.preferenciasVersion, ...borrador };
  const sinGuardar = Object.keys(borrador).length > 0;
  const set = (c: Partial<PreferenciasVersion>) => setBorrador({ ...borrador, ...c });

  /** Dos listas por característica: lo que se quiere y lo que se tolera. Lo que no esté en
   *  ninguna se pinta en rojo, que es lo que evita una tercera lista de descartes. */
  const Par = ({ titulo, ayuda, ideal, aceptable }: {
    titulo: string;
    ayuda: string;
    ideal: React.ReactNode;
    aceptable: React.ReactNode;
  }) => (
    <Box>
      <Typography variant="subtitle2">{titulo}</Typography>
      <Typography variant="caption" color="text.secondary">{ayuda}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
        <Box sx={{ flex: 1 }}>{ideal}</Box>
        <Box sx={{ flex: 1 }}>{aceptable}</Box>
      </Stack>
    </Box>
  );

  const listaContenedores = (
    valor: string[],
    etiqueta: string,
    cambiar: (v: string[]) => void,
  ) => (
    <Autocomplete
      multiple freeSolo size="small"
      options={['mkv', 'mp4', 'avi']}
      value={valor}
      // Se normaliza lo tecleado: `.MKV` y `mkv` son el mismo contenedor, y guardarlos
      // distintos dejaría de casar con lo que manda el parser, que va en minúsculas.
      onChange={(_, v) =>
        cambiar(v.map((x) => normalizarContenedor(String(x))).filter((x): x is string => !!x))
      }
      renderInput={(params) => <TextField {...params} label={etiqueta} />}
    />
  );

  const listaCodecs = (valor: string[], etiqueta: string, cambiar: (v: string[]) => void) => (
    <Autocomplete
      multiple size="small"
      options={Object.keys(NOMBRE_FAMILIA)}
      getOptionLabel={(k) => NOMBRE_FAMILIA[k] ?? k}
      value={valor}
      onChange={(_, v) => cambiar(v)}
      renderInput={(params) => <TextField {...params} label={etiqueta} />}
    />
  );

  const listaIdiomas = (valor: string[], etiqueta: string, cambiar: (v: string[]) => void) => (
    <Autocomplete
      multiple freeSolo size="small"
      options={Object.keys(NOMBRE_IDIOMA)}
      // La etiqueta LLEVA el código dentro a propósito: lo que se guarda es el código, y
      // un chip que solo dijera «castellano» no dejaría ver que el valor es `es`.
      getOptionLabel={(k) => (NOMBRE_IDIOMA[String(k)] ? `${NOMBRE_IDIOMA[String(k)]} (${k})` : String(k))}
      value={valor}
      onChange={(_, v) => cambiar(v.map((x) => String(x).trim().toLowerCase()).filter(Boolean))}
      renderInput={(params) => <TextField {...params} label={etiqueta} />}
    />
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Qué versión te interesa</Typography>
      <Typography variant="caption" color="text.secondary">
        La ficha de una película ofrece diez o quince versiones que solo se distinguen por
        letra pequeña. Esto es lo que pinta cada característica en verde, ámbar o rojo y lo
        que sube arriba las más idóneas. No descarta nada: se puede seguir descargando lo que
        salga en rojo. Las versiones 3D son la excepción: salen siempre en rojo y nunca se
        bajan solas, sin ajuste que lo cambie.
      </Typography>

      <Stack spacing={3} sx={{ mt: 2 }}>
        <Box>
          <Typography variant="subtitle2">Resolución</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              select size="small" label="La que quieres"
              value={p.resolucionIdeal}
              onChange={(e) => set({ resolucionIdeal: e.target.value as PreferenciasVersion['resolucionIdeal'] })}
              sx={{ minWidth: 160 }}
            >
              {RESOLUTION_LADDER.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label={`Por encima de ${p.resolucionIdeal}`}
              value={p.resolucionPorEncima}
              onChange={(e) => set({ resolucionPorEncima: e.target.value as PreferenciasVersion['resolucionPorEncima'] })}
              sx={{ minWidth: 200 }}
            >
              {NIVELES.map((n) => <MenuItem key={n.v} value={n.v}>{n.t}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label={`Por debajo de ${p.resolucionIdeal}`}
              value={p.resolucionPorDebajo}
              onChange={(e) => set({ resolucionPorDebajo: e.target.value as PreferenciasVersion['resolucionPorDebajo'] })}
              sx={{ minWidth: 200 }}
            >
              {NIVELES.map((n) => <MenuItem key={n.v} value={n.v}>{n.t}</MenuItem>)}
            </TextField>
          </Stack>
        </Box>

        <Divider />

        <Par
          titulo="Contenedor"
          ayuda="La extensión del fichero. Lo que no esté en ninguna de las dos listas sale en rojo."
          ideal={listaContenedores(p.contenedoresIdeales, 'Los que quieres', (v) => set({ contenedoresIdeales: v }))}
          aceptable={listaContenedores(p.contenedoresAceptables, 'Los que te valen', (v) => set({ contenedoresAceptables: v }))}
        />

        <Par
          titulo="Códec de vídeo"
          ayuda="Se compara por familia: x264, H.264 y AVC son lo mismo, y `x265 10Bit Dolby Vision HDR` cuenta como H.265."
          ideal={listaCodecs(p.codecsIdeales, 'Los que quieres', (v) => set({ codecsIdeales: v }))}
          aceptable={listaCodecs(p.codecsAceptables, 'Los que te valen', (v) => set({ codecsAceptables: v }))}
        />

        <Par
          titulo="Idiomas que entiendes"
          ayuda="Valen para el audio y para los subtítulos: la pregunta es la misma. Lo que cambia es la consecuencia — sin audio que entiendas la versión no vale, y sin subtítulos, como mucho, es mejorable. El latino tiene bandera propia en HispaShare y es un idioma aparte del castellano."
          ideal={listaIdiomas(p.audioIdeal, 'Los que quieres', (v) => set({ audioIdeal: v }))}
          aceptable={listaIdiomas(p.audioAceptable, 'Los que te valen', (v) => set({ audioAceptable: v }))}
        />

        <Box sx={{ mt: -1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={p.exigirIdiomaOriginal}
                onChange={(e) => set({ exigirIdiomaOriginal: e.target.checked })}
              />
            }
            label="Que traiga además el idioma original"
          />
          <Typography variant="caption" color="text.secondary" component="div">
            Con esto, una versión que solo trae el doblaje se queda en ámbar: se entiende, pero
            no es como se rodó. Lo mismo con los subtítulos, que son verdes cuando están en tu
            idioma y en el original. Una película española no pierde nada, porque ahí el
            castellano ya es el original. El idioma original sale del país que HispaShare pone junto al
            título original —<i>Spider-Man (EE.UU.)</i>—, así que cuando la ficha no lo dice se
            mira lo que hay: con castellano y algún otro audio se da por bueno, y con castellano
            a secas queda la duda, que es el ámbar.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2">Tamaño</Typography>
          <Typography variant="caption" color="text.secondary">
            No hay un número de gigas bueno: depende de lo que dure la película. Se mide el
            <b> bitrate</b> —lo que ocupa un vídeo dividido entre la duración de la ficha— y se
            compara con lo normal en su resolución, corregido por códec: x265 hace lo mismo con
            la mitad. Las bandas están medidas sobre 4237 versiones de tu propia lista, así que
            lo que sale en rojo es la cola de verdad. Si la ficha no dice la duración, gris.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <TextField
              select size="small" label="Cuánto se aprieta"
              value={p.toleranciaTamano}
              onChange={(e) => set({ toleranciaTamano: e.target.value as PreferenciasVersion['toleranciaTamano'] })}
              sx={{ minWidth: 320 }}
            >
              {TOLERANCIAS.map((t) => <MenuItem key={t.v} value={t.v}>{t.t}</MenuItem>)}
            </TextField>
          </Box>
          {/* Qué significa el ajuste EN NÚMEROS, para la resolución que se está buscando.
              Un desplegable de tres palabras no dice qué va a pasar; esto sí. */}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Con esto, un {p.resolucionIdeal} en x264 se pinta en verde entre{' '}
            <b>{muestraBanda(p, 'h264')}</b> y en x265 entre <b>{muestraBanda(p, 'h265')}</b>.
          </Typography>
        </Box>

        {guardar.isError && <Alert severity="error">{(guardar.error as ApiFallo).message}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" size="small"
            disabled={!sinGuardar || guardar.isPending}
            onClick={() => guardar.mutate(p)}
          >
            Guardar
          </Button>
          {sinGuardar && <Button size="small" onClick={() => setBorrador({})}>Deshacer</Button>}
          <Button size="small" onClick={() => set(PREFERENCIAS_VERSION_POR_DEFECTO)}>
            Valores de partida
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** La descarga automática (D-22).
 *
 *  Lo que se enseña aquí no son cuatro ajustes: es cuánto disco puede gastar algo que
 *  decide sin que nadie mire. Por eso los tres frenos —modo, cupo y suelo de disco— van
 *  juntos y con sus consecuencias escritas al lado, y por eso la lista de decisiones está
 *  debajo: en modo simulación es lo único que produce. */
function SeccionAutomatica() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ajustes'], queryFn: api.ajustes });
  const decisiones = useQuery({ queryKey: ['auto-decisiones'], queryFn: api.decisionesAuto });
  const [borrador, setBorrador] = useState<Partial<AjustesApp>>({});

  const guardar = useMutation({
    mutationFn: (c: Partial<AjustesApp>) => api.guardarAjustes(c),
    onSuccess: () => {
      setBorrador({});
      void qc.invalidateQueries({ queryKey: ['ajustes'] });
    },
  });

  if (!data) return null;
  const a = { ...data.settings, ...borrador };
  const sinGuardar = Object.keys(borrador).length > 0;
  const set = (c: Partial<AjustesApp>) => setBorrador({ ...borrador, ...c });
  const filas = decisiones.data?.decisions ?? [];
  const cuenta = (r: DecisionAuto['result']) => filas.filter((d) => d.result === r).length;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Descarga automática</Typography>
      <Typography variant="caption" color="text.secondary">
        Solo se descarga sola una película cuando no hay nada que decidir: la ficha cruza por
        IMDb, la versión no tiene ninguna característica en rojo, se sabe su resolución y su
        audio, viene en un solo fichero y no hay otra versión a la par. Del resto se hace una
        recomendación y eliges tú.
      </Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Box>
          <TextField
            select size="small" label="Hasta dónde llega"
            value={a.autoModo}
            onChange={(e) => set({ autoModo: e.target.value as AjustesApp['autoModo'] })}
            sx={{ minWidth: 360 }}
          >
            {MODOS_AUTO.map((m) => <MenuItem key={m.v} value={m.v}>{m.t}</MenuItem>)}
          </TextField>
        </Box>

        {a.autoModo === 'simulacion' && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No se encola nada. Cada barrido apunta qué habría hecho y se ve abajo: es la forma
            de comprobar durante unas semanas que elige lo que tú elegirías, sin arriesgar un
            byte.
          </Alert>
        )}
        {a.autoModo === 'activo' && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Se encolarán películas sin preguntar, hasta {a.autoActivas} a la vez y mientras
            queden más de {a.autoMinLibreGb} GB libres contando lo que ya está comprometido.
          </Alert>
        )}

        <Divider />

        <Typography variant="subtitle2">Los dos frenos que protegen el disco</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          El primero limita cuántas bajan a la vez; el segundo, cuánto se puede llegar a
          ocupar. Hacen falta los dos: un cupo bajo no impide llenar el disco, solo lo hace
          más despacio.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small" type="number" label="Descargas a la vez"
            value={a.autoActivas}
            onChange={(e) => set({ autoActivas: Math.max(1, Number(e.target.value)) })}
            helperText="Entra una nueva cuando termina otra"
            sx={{ maxWidth: 220 }}
          />
          <TextField
            size="small" type="number" label="Dejar siempre libres (GB)"
            value={a.autoMinLibreGb}
            onChange={(e) => set({ autoMinLibreGb: Math.max(0, Number(e.target.value)) })}
            helperText="Contando lo que la cola ya se va a comer"
            sx={{ maxWidth: 280 }}
          />
        </Stack>

        <Alert severity="info" sx={{ py: 0.5 }}>
          El hueco no se mide con lo que dice el disco ahora: a lo libre se le resta lo que
          falta por bajar de la cola y lo que las conversiones van a duplicar mientras el
          original sigue sembrando, y cada película reserva <b>el doble</b> de su tamaño. Si
          no se puede leer el espacio libre, no se encola nada.
        </Alert>

        <Divider />

        <Typography variant="subtitle2">Cuándo se fía de un release</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small" type="number" label="Descargas mínimas"
            value={a.autoMinDescargas}
            onChange={(e) => set({ autoMinDescargas: Math.max(0, Number(e.target.value)) })}
            helperText="En ed2k, sin fuentes no llega nunca"
            sx={{ maxWidth: 220 }}
          />
          <TextField
            size="small" type="number" label="Factor de salud"
            value={a.autoFactorSalud}
            onChange={(e) => set({ autoFactorSalud: Math.max(2, Number(e.target.value)) })}
            helperText="Veces más descargada para preferirla si solo pierde en subtítulos"
            sx={{ maxWidth: 320 }}
          />
        </Stack>

        {guardar.isError && <Alert severity="error">{(guardar.error as ApiFallo).message}</Alert>}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" size="small"
            disabled={!sinGuardar || guardar.isPending}
            onClick={() => guardar.mutate(borrador)}
          >
            Guardar
          </Button>
          {sinGuardar && <Button size="small" onClick={() => setBorrador({})}>Deshacer</Button>}
        </Stack>

        <Divider />

        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">Últimas decisiones</Typography>
          {filas.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {cuenta('encolada')} encoladas · {cuenta('simulada')} se habrían encolado ·{' '}
              {cuenta('recomendada')} para ti · {cuenta('sin_candidata')} sin nada que valga
            </Typography>
          )}
        </Stack>

        {!filas.length ? (
          <Typography variant="caption" color="text.secondary">
            Todavía no hay ninguna. Se apuntan en cada barrido de la watchlist.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Película</TableCell>
                  <TableCell>Qué se hizo</TableCell>
                  <TableCell align="right">Puntos</TableCell>
                  <TableCell>Por qué</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.slice(0, 100).map((d) => (
                  <TableRow key={d.id}>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {d.hispashareId ? (
                        <Link component={RouterLink} to={`/pelicula/${d.hispashareId}`}>
                          {d.title ?? '—'}
                        </Link>
                      ) : (d.title ?? '—')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={COLOR_DECISION[d.result]}
                        variant={d.result === 'encolada' ? 'filled' : 'outlined'}
                        label={ETIQUETA_DECISION[d.result]}
                      />
                      {/* El freno importa tanto como el resultado: «se habría encolado, pero
                          no cabía» y «se habría encolado, pero estamos en simulación» son
                          dos cosas muy distintas. */}
                      {d.brake && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          {FRENO[d.brake] ?? d.brake}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{d.points ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{d.reason ?? '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
}

/** El disco, agrupado por sistema de ficheros.
 *
 *  Agrupado y no por carpeta a propósito: con las tres carpetas de D-15 en el mismo disco
 *  —que es como tienen que estar— el espacio es UNO, y pintarlo tres veces haría creer que
 *  hay el triple. Que salgan en barras separadas no es cosmética: significa que colocar una
 *  película copia varios gigas en vez de renombrarla, y que el enlace duro con aMule se
 *  pierde por el camino. */
function SeccionAlmacenamiento() {
  const { data, isError, error } = useQuery({
    queryKey: ['storage'],
    queryFn: api.almacenamiento,
    refetchInterval: 60_000,
  });

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Espacio en disco</Typography>
      {/* Un error se dice. Girando para siempre, el síntoma de una API vieja —que no
          conoce `/api/storage`— es idéntico al de un disco que tarda en responder. */}
      {isError ? (
        <Alert severity="error">No se pudo leer el disco: {(error as ApiFallo).message}</Alert>
      ) : !data ? (
        <CircularProgress size={22} />
      ) : (
        <Stack spacing={2}>
          {data.sistemas.map((sf) => {
            // El umbral de D-14 solo dice algo donde caen las descargas: es el disco que
            // la recogida mira para decidir si retira originales antes de tiempo.
            const bajoUmbral =
              sf.carpetas.some((c) => c.rol === 'descargas') && sf.bytesLibres < data.minLibreGb * GB;
            return (
              <Stack key={sf.id} spacing={0.75}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0, sm: 1 }}
                  sx={{ justifyContent: 'space-between', alignItems: { sm: 'baseline' } }}
                >
                  <Typography variant="body2">
                    {formatearTamano(sf.bytesLibres)} libres de {formatearTamano(sf.bytesTotales)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sf.porcentajeUsado.toFixed(0)}% usado · {formatearTamano(sf.bytesUsados)} ocupados
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, sf.porcentajeUsado)}
                  color={bajoUmbral || sf.porcentajeUsado >= 95 ? 'error' : sf.porcentajeUsado >= 85 ? 'warning' : 'primary'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {sf.carpetas.map((c) => (
                    // La ruta va en el tooltip y no en la etiqueta: son rutas largas y
                    // repetidas, y a 375 px se comen la fila entera.
                    <Tooltip key={`${c.rol}:${c.ruta}`} title={c.ruta}>
                      <Chip size="small" variant="outlined" label={c.nombre} />
                    </Tooltip>
                  ))}
                </Stack>
                {bajoUmbral && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    Por debajo del umbral de {data.minLibreGb} GB: la recogida retirará originales
                    antiguos aunque todavía no les tocara.
                  </Alert>
                )}
              </Stack>
            );
          })}

          {data.sistemas.length > 1 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Las carpetas están repartidas en {data.sistemas.length} sistemas de ficheros. Colocar
              una película entre dos de ellos copia el fichero entero en vez de renombrarlo, y tarda
              lo que tarde el disco (D-15).
            </Alert>
          )}

          {data.inaccesibles.map((c) => (
            <Alert key={`${c.rol}:${c.ruta}`} severity="error" sx={{ py: 0.5 }}>
              No se puede leer «{c.nombre}» en{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{c.ruta}</Box> ({c.error}): o no
              está montada, o la ruta local no es la que ve esta máquina.
            </Alert>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export function Ajustes() {
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ name: '', baseUrl: 'http://127.0.0.1:32400', token: '' });
  /** Id del servidor cuyo token se está reemplazando, o null si no hay ninguno. */
  const [tokenDe, setTokenDe] = useState<string | null>(null);
  const [tokenNuevo, setTokenNuevo] = useState('');
  /** PIN en curso, mientras alguien lo teclea en plex.tv/link. */
  const [pin, setPin] = useState<PinPlex | null>(null);
  const [ahora, setAhora] = useState(() => Date.now());

  const salud = useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 15_000 });
  const servidores = useQuery({ queryKey: ['plex-servers'], queryFn: api.servidoresPlex });
  const bibliotecas = useQuery({ queryKey: ['plex-libraries'], queryFn: api.bibliotecasPlex });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['plex-servers'] });
    void qc.invalidateQueries({ queryKey: ['plex-libraries'] });
    void qc.invalidateQueries({ queryKey: ['health'] });
  };

  const anadir = useMutation({
    mutationFn: () => api.anadirServidorPlex(form),
    onSuccess: () => {
      setNuevo(false);
      setForm({ name: '', baseUrl: 'http://127.0.0.1:32400', token: '' });
      refrescar();
    },
  });
  const borrar = useMutation({ mutationFn: api.borrarServidorPlex, onSuccess: refrescar });
  const cerrarTokenDialogo = () => {
    setTokenDe(null);
    setTokenNuevo('');
    setPin(null);
  };

  const reemplazarToken = useMutation({
    mutationFn: () => api.reemplazarTokenPlex(tokenDe!, tokenNuevo),
    onSuccess: () => {
      cerrarTokenDialogo();
      refrescar();
    },
  });

  const vincular = useMutation({
    mutationFn: () => api.vincularPlex(tokenDe!),
    onSuccess: (r) => setPin(r.pin),
  });

  // El sondeo lo lleva react-query y no un setInterval a mano: así no se solapan dos
  // vueltas, se corta solo al cerrar el diálogo y el error de una no dispara las
  // siguientes. Se para al primer fallo y al vincularse; si no, seguiría llamando para
  // siempre a un PIN que ya no existe.
  const vinculacion = useQuery({
    queryKey: ['plex-link', tokenDe, pin?.id],
    queryFn: () => api.comprobarVinculacionPlex(tokenDe!, pin!.id),
    enabled: tokenDe !== null && pin !== null,
    retry: false,
    gcTime: 0,
    refetchInterval: (q) => (q.state.error || q.state.data?.pendiente === false ? false : 2000),
  });

  useEffect(() => {
    if (vinculacion.data && !vinculacion.data.pendiente) {
      cerrarTokenDialogo();
      refrescar();
    }
    // Depende solo del dato: lo que dispara esto es que llegue una respuesta ya vinculada,
    // no que se haya vuelto a pintar la pantalla.
  }, [vinculacion.data]);

  // Solo para la cuenta atrás del código, que caduca a los 15 minutos.
  useEffect(() => {
    if (!pin) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pin]);
  const redescubrir = useMutation({ mutationFn: api.redescubrirPlex, onSuccess: refrescar });
  const sincronizar = useMutation({ mutationFn: api.sincronizarPlex, onSuccess: refrescar });

  const s = salud.data;
  const marcadas = bibliotecas.data?.libraries.filter((l) => l.countsAsOwned) ?? [];

  const segundosPin = pin
    ? Math.max(0, Math.round((new Date(pin.expiresAt).getTime() - ahora) / 1000))
    : 0;
  const cuentaAtras = `${Math.floor(segundosPin / 60)}:${String(segundosPin % 60).padStart(2, '0')}`;

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Ajustes</Typography>

      {/* ── Diagnóstico ───────────────────────────────────────────── */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Estado de los servicios</Typography>
        {!s ? (
          <CircularProgress size={22} />
        ) : (
          <Stack spacing={1}>
            {[
              { n: 'Base de datos', e: s.db, d: null },
              { n: 'aMule', e: s.amule.status, d: s.amule.detail ?? `${s.amule.ed2k ?? '—'} · Kad ${s.amule.kad ?? '—'}` },
              { n: 'Plex', e: s.plex.status, d: s.plex.detail ?? `${s.plex.countedLibraries} biblioteca(s) · ${s.plex.lastSync ? 'sync ' + new Date(s.plex.lastSync).toLocaleString('es-ES') : 'sin sincronizar'}` },
              { n: 'HispaShare', e: s.indexer.status, d: s.indexer.detail },
              // No es un servicio: es si esta máquina puede colocar una película. Se
              // comprueba aquí porque el sitio donde se descubre solo, si no, es con una
              // película ya convertida esperando y el fallo delante.
              { n: 'Colocación', e: s.colocacion.status, d: s.colocacion.detail },
            ].map((x) => (
              // El detalle es lo que dice QUÉ está mal, así que a 375 px baja a su propia
              // línea en vez de quedarse en una columna de dos palabras de ancho (X-3).
              <Stack key={x.n} spacing={0.25}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={x.e}
                    color={x.e === 'ok' ? 'success' : x.e === 'degraded' ? 'warning' : x.e === 'down' ? 'error' : 'default'}
                    sx={{ minWidth: 96 }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 110 }}>{x.n}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  >
                    {x.d}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: 'block', sm: 'none' }, pl: 0.5 }}
                >
                  {x.d}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ── Espacio en disco ─────────────────────────────────────── */}
      <SeccionAlmacenamiento />

      {/* ── Servidores Plex ───────────────────────────────────────── */}
      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1, mb: 1,
          }}
        >
          <Typography variant="subtitle1">Servidores Plex</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={sincronizar.isPending || !marcadas.length} onClick={() => sincronizar.mutate()}>
              {sincronizar.isPending ? 'Sincronizando…' : 'Sincronizar ahora'}
            </Button>
            <Button size="small" variant="outlined" onClick={() => setNuevo(true)}>Añadir servidor</Button>
          </Stack>
        </Stack>

        {sincronizar.data && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {sincronizar.data.items} películas de {sincronizar.data.bibliotecas} biblioteca(s).
          </Alert>
        )}

        {!marcadas.length && (bibliotecas.data?.libraries.length ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ninguna biblioteca marcada como «Cuenta»: la app no puede saber qué películas ya
            tienes, y lo dirá en las búsquedas en vez de responder «no la tienes» a todo.
          </Alert>
        )}

        {servidores.data?.servers.length === 0 && (
          <Alert severity="info">Sin servidores. Añade uno para que la app sepa qué tienes.</Alert>
        )}

        {servidores.data?.servers.map((srv) => (
          <Accordion key={srv.id} defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{srv.name}</Typography>
                <Chip size="small" color={srv.lastError ? 'error' : 'success'} label={srv.lastError ? 'con errores' : 'conectado'} />
                <Typography variant="caption" color="text.secondary">{srv.baseUrl}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Fila etiqueta="Versión" valor={srv.version ?? '—'} />
                {/* El token es de solo escritura: la API no lo devuelve nunca. */}
                <Fila etiqueta="Token" valor="•••••••• (guardado cifrado)" />
                <Fila etiqueta="Última conexión" valor={srv.lastOkAt ? new Date(srv.lastOkAt).toLocaleString('es-ES') : '—'} />
                {srv.lastError && <Alert severity="error">{srv.lastError}</Alert>}

                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => redescubrir.mutate(srv.id)}>Redescubrir bibliotecas</Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setTokenDe(srv.id);
                      setTokenNuevo('');
                      setPin(null);
                      reemplazarToken.reset();
                      vincular.reset();
                    }}
                  >
                    Reemplazar token
                  </Button>
                  <Button size="small" color="error" onClick={() => borrar.mutate(srv.id)}>Borrar</Button>
                </Stack>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Bibliotecas</Typography>
                {bibliotecas.data?.libraries
                  .filter((l) => l.serverId === srv.id)
                  .map((l) => (
                    <Box key={l.id}>
                      <BibliotecaFila lib={l} onCambio={refrescar} />
                      <Divider />
                    </Box>
                  ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* ── aMule ────────────────────────────────────────────────── */}
      <SeccionAmule />

      {/* ── Conversión a MP4 ─────────────────────────────────────── */}
      <SeccionConversion />

      <SeccionVersiones />

      <SeccionAutomatica />

      <Alert severity="info">
        Las credenciales de HispaShare y aMule no se editan aquí: viven en el entorno del
        servidor porque son una sola cuenta y un servicio del propio Compose. Plex es distinto
        porque puedes tener varios y hace falta poder verificarlos.
      </Alert>

      {/* ── Alta de servidor ──────────────────────────────────────── */}
      <Dialog open={nuevo} onClose={() => setNuevo(false)} fullWidth maxWidth="sm">
        <DialogTitle>Añadir un servidor Plex</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="Nombre" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField size="small" label="URL" value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
            <TextField size="small" label="Token" type="password" value={form.token}
              onChange={(e) => setForm({ ...form, token: e.target.value })}
              helperText="En Plex: una película → ··· → Obtener información → Ver XML. El token va en la URL." />
            {anadir.isError && <Alert severity="error">{(anadir.error as ApiFallo).message}</Alert>}
            <Alert severity="info" sx={{ py: 0.5 }}>
              Se prueba la conexión antes de guardar: si falla, no se guarda nada.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevo(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!form.name || !form.token || anadir.isPending}
            onClick={() => anadir.mutate()}>
            {anadir.isPending ? 'Probando…' : 'Probar y guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reemplazo del token ───────────────────────────────────── */}
      <Dialog open={tokenDe !== null} onClose={cerrarTokenDialogo} fullWidth maxWidth="sm">
        <DialogTitle>Reemplazar el token de Plex</DialogTitle>
        <DialogContent>
          {!pin ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Vincular es lo recomendado: el token nace como dispositivo propio de esta
                aplicación. El del «Ver XML» pertenece a la sesión del navegador y muere al
                cerrarla, aunque aquí siga haciendo falta.
              </Alert>
              <Button variant="contained" disabled={vincular.isPending}
                onClick={() => vincular.mutate()}>
                {vincular.isPending ? 'Pidiendo código…' : 'Vincular con Plex'}
              </Button>
              {vincular.isError && (
                <Alert severity="error">{(vincular.error as ApiFallo).message}</Alert>
              )}

              <Divider>o pega un token a mano</Divider>

              <TextField size="small" label="Token nuevo" type="password" value={tokenNuevo}
                onChange={(e) => setTokenNuevo(e.target.value)}
                helperText="En Plex: una película → ··· → Obtener información → Ver XML. El token va en la URL." />
              {reemplazarToken.isError && (
                <Alert severity="error">{(reemplazarToken.error as ApiFallo).message}</Alert>
              )}
              <Alert severity="info" sx={{ py: 0.5 }}>
                Sea cual sea el camino, se prueba leyendo las bibliotecas antes de guardar. Solo
                cambia la credencial: las bibliotecas, lo que cuenta como poseído y las rutas ya
                verificadas se conservan.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ alignItems: 'center', mt: 1, textAlign: 'center' }}>
              <Typography variant="body2">
                Entra en{' '}
                <Link href="https://plex.tv/link" target="_blank" rel="noreferrer">plex.tv/link</Link>
                {' '}con la cuenta dueña del servidor y teclea este código:
              </Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 44, fontWeight: 700, letterSpacing: 10, pl: '10px' }}>
                {pin.code}
              </Typography>
              {segundosPin > 0 && <LinearProgress sx={{ width: '100%' }} />}
              <Typography variant="caption" color="text.secondary">
                {segundosPin > 0
                  ? `Esperando a que lo introduzcas… el código caduca en ${cuentaAtras}`
                  : 'El código ha caducado. Cierra y pide otro.'}
              </Typography>
              {vinculacion.error && (
                <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                  {(vinculacion.error as ApiFallo).message}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarTokenDialogo}>Cancelar</Button>
          {!pin && (
            <Button disabled={!tokenNuevo || reemplazarToken.isPending}
              onClick={() => reemplazarToken.mutate()}>
              {reemplazarToken.isPending ? 'Probando…' : 'Probar y guardar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
